import { useMemo, useState } from 'react';
import { charactersList } from '@/mocks/characters';
import CharacterCard from '@/components/feature/CharacterCard';
import Pagination from '@/components/base/Pagination';
import PageHeader from '@/components/feature/PageHeader';
import EmptyState from '@/components/feature/EmptyState';
import CharacterFilter from '@/pages/characters/components/CharacterFilter';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 8;

const elements = Array.from(new Set(charactersList.map((c) => c.element)));
const rarities = [
  { value: 'all', label: '全部' },
  { value: '5', label: '五星' },
  { value: '4', label: '四星' },
];

export default function Characters() {
  usePageMeta({
    title: '原神角色攻略大全 - 深入解析角色技能与玩法',
    description:
      '原神角色攻略大全收录热门角色的技能机制、天赋加点、圣遗物与配队思路，支持按元素属性与稀有度筛选，助你培养最强队伍。',
    keywords: '原神角色,原神角色攻略,角色技能,配队,圣遗物',
  });

  const [element, setElement] = useState('全部');
  const [rarity, setRarity] = useState('all');
  const [page, setPage] = useState(1);

  const filtered = useMemo(
    () =>
      charactersList.filter((c) => {
        const matchElement = element === '全部' || c.element === element;
        const matchRarity = rarity === 'all' || String(c.rarity) === rarity;
        return matchElement && matchRarity;
      }),
    [element, rarity],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleElement = (value: string) => {
    setElement(value);
    setPage(1);
  };
  const handleRarity = (value: string) => {
    setRarity(value);
    setPage(1);
  };
  const reset = () => {
    setElement('全部');
    setRarity('all');
    setPage(1);
  };

  return (
    <div>
      <PageHeader
        crumbs={[{ label: '首页', to: '/' }, { label: '角色攻略' }]}
        icon="ri-user-star-line"
        title="角色攻略大全"
        description="按元素属性与稀有度筛选心仪的角色，点击卡片查看技能机制、天赋加点与配队思路。"
      />

      <div className="container-page py-8 md:py-12">
        <CharacterFilter
          elements={elements}
          element={element}
          onElement={handleElement}
          rarities={rarities}
          rarity={rarity}
          onRarity={handleRarity}
          total={filtered.length}
        />

        <div className="mt-6">
          {pageItems.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {pageItems.map((character) => (
                <CharacterCard key={character.id} character={character} />
              ))}
            </div>
          ) : (
            <EmptyState
              icon="ri-user-search-line"
              title="没有符合条件的角色"
              description="当前筛选条件下暂无角色，试试切换元素或稀有度，或者重置筛选。"
              actionLabel="重置筛选"
              onAction={reset}
            />
          )}
        </div>

        {filtered.length > 0 && (
          <div className="mt-8">
            <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          </div>
        )}
      </div>
    </div>
  );
}